import { createAsyncThunk } from "@reduxjs/toolkit";

import * as basketAPI from "../../../api/basket";
// Import the concrete module, not the `apps/checkout/thunks` barrel —
// that barrel re-exports this file, so it would create a cycle.
import { loadBasket } from "../../../apps/checkout/thunks/loadBasket";
import { IBasket } from "../../../models/catalogue";
import { IProductAPIURL } from "../../../models/nominals";

/**
 * Arguments for addBasketLine thunk.
 */
interface AddBasketLineArgs {
    productURL: IProductAPIURL;
    qty?: number;
}

/**
 * Add a product to the basket.
 *
 * This thunk adds a single product to the user's basket and reloads
 * the basket to reflect the updated state. This is the primary method
 * for adding products from product pages, configurators, and gift selections.
 *
 * @param {IProductAPIURL} args.productURL - API URL of the product to add
 * @param {number} [args.qty=1] - Quantity to add (defaults to 1)
 *
 * @example
 * // Add one product to basket
 * dispatch(addBasketLine({ productURL: product.url }));
 *
 * @example
 * // Add multiple quantities
 * dispatch(addBasketLine({ productURL: product.url, qty: 3 }));
 *
 * @sideEffects
 * - Makes API call to add product to basket via basketAPI.addProduct
 * - Dispatches loadBasket thunk to reload basket data
 * - Updates `data.basket` with reloaded basket via extraReducers
 */
// Prefix kept as "checkout/": module only moved, and nothing matches these
// types by string, so renaming would change them unnoticed.
export const addBasketLine = createAsyncThunk(
    "checkout/addBasketLine",
    async (args: AddBasketLineArgs, { dispatch }): Promise<IBasket | null> => {
        await basketAPI.addProduct(args.productURL, args.qty);
        const result = await dispatch(loadBasket()).unwrap();
        return result.basket;
    },
);
